import { useParams, Link } from 'react-router-dom';
import { articleMap, latestArticles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/feature/StructuredData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <p className="text-foreground-500">文章未找到</p>
      </div>
    );
  }

  const relatedArticles = latestArticles
    .filter((a) => a.slug !== article.slug && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    url: `${siteInfo.url}/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.slug}`,
    },
    image: article.imageUrl,
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteInfo.url,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `${siteInfo.url}/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <div className="min-h-screen pt-16">
      <StructuredData data={{ ...articleStructuredData }} />

      <article>
        <div className="bg-background-100/70 border-b border-background-200/70">
          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: article.categoryName, path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {article.categoryName}
              </Link>
              {article.featured && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                  精选
                </span>
              )}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-500 leading-relaxed mb-4">
              {article.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="hidden sm:inline">·</span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> 发布于 {article.publishedAt}
              </span>
              <span className="hidden sm:inline">·</span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime} 分钟阅读
              </span>
              <span className="hidden sm:inline">·</span>
              <span className="flex items-center gap-1">
                <i className="ri-refresh-line"></i> 更新于 {article.updatedAt}
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-[900px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1 min-w-0">
              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <h4 className="font-heading font-semibold text-foreground-900 mb-3">文章标签</h4>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <a
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-secondary-100 hover:text-secondary-700 transition-colors cursor-pointer"
                    aria-label="复制链接"
                  >
                    <i className="ri-link text-sm"></i>
                  </a>
                  <a
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-secondary-100 hover:text-secondary-700 transition-colors cursor-pointer"
                    aria-label="微信分享"
                  >
                    <i className="ri-wechat-line text-sm"></i>
                  </a>
                  <a
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-secondary-100 hover:text-secondary-700 transition-colors cursor-pointer"
                    aria-label="微博分享"
                  >
                    <i className="ri-weibo-line text-sm"></i>
                  </a>
                </div>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  返回 {article.categoryName} <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="bg-background-100/70 py-14 md:py-18">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="group bg-background-50 rounded-xl p-5 border border-background-200/70 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                >
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                    {rel.title}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                    {rel.excerpt}
                  </p>
                  <span className="text-xs text-foreground-400">{rel.publishedAt}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}